import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IPermission, IPolicy, IService } from '@shared/models/scim-admin';
import { forkJoin, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ScimAdminService } from '../scim-admin.service';

export interface IPermissionGroup {
	serviceName: string;
	permissions: IPermission[];
}

@Component({
	selector: 'app-policy-detail-dialog',
	standalone: false,
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './policy-detail-dialog.component.html',
})
export class PolicyDetailDialogComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();

	permissionGroups: IPermissionGroup[] = [];
	isLoadingPermissions = true;

	constructor(
		@Inject(MAT_DIALOG_DATA) public policy: IPolicy,
		public dialogRef: MatDialogRef<PolicyDetailDialogComponent>,
		private scimAdminService: ScimAdminService,
	) {}

	ngOnInit(): void {
		this.loadPermissions();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private loadPermissions(): void {
		if (!this.policy.permissions || this.policy.permissions.length === 0) {
			this.isLoadingPermissions = false;
			return;
		}

		this.scimAdminService
			.getServices()
			.pipe(take(1))
			.subscribe({
				next: (services) => {
					if (services.length === 0) {
						this.isLoadingPermissions = false;
						return;
					}
					const permissionRequests = services.map((s) => this.scimAdminService.getServicePermissions(s.id));
					forkJoin(permissionRequests)
						.pipe(take(1))
						.subscribe({
							next: (allPermissions) => {
								this.resolvePermissions(services, allPermissions);
								this.isLoadingPermissions = false;
							},
							error: () => {
								this.isLoadingPermissions = false;
							},
						});
				},
				error: () => {
					this.isLoadingPermissions = false;
				},
			});
	}

	private resolvePermissions(services: IService[], allPermissions: IPermission[][]): void {
		const policyPermissionIds = new Set(this.policy.permissions || []);
		const groups: IPermissionGroup[] = [];

		services.forEach((service, index) => {
			const servicePermissions = allPermissions[index] || [];
			const matchedPermissions = servicePermissions.filter((p) => policyPermissionIds.has(p.id));
			if (matchedPermissions.length > 0) {
				groups.push({
					serviceName: service.name,
					permissions: matchedPermissions,
				});
			}
		});

		this.permissionGroups = groups;
	}

	onClose(): void {
		this.dialogRef.close();
	}
}
